/*
 * XYON CUSTOM: discount-code-capture
 * Validates a typed discount code via the Storefront API against a throwaway cart
 * (cartCreate -> cartDiscountCodesUpdate), reading cart.warnings to decide block vs accept.
 * The visitor's live cart is never touched. On accept it sets the handoff cookie and forwards
 * to the consultation with the code as a URL param. Every outcome pushes a preferred_code_result
 * event to the GTM dataLayer. Config arrives via data-* attributes from
 * sections/discount-code-capture.liquid.
 */
if (!customElements.get('discount-code-capture')) {
  // Declared inside the guard, not at top level: the section embeds its own script tag, so a page
  // with two instances loads this file twice, and a top-level const would throw on redeclaration
  // before execution ever reached the customElements.get guard that exists to allow that.
  const DATALAYER_EVENT_RESULT = 'preferred_code_result';
  const OUTCOME_ACCEPTED = 'accepted';
  const OUTCOME_REJECTED = 'rejected';
  const OUTCOME_ERROR = 'error';
  const REASON_API_ERROR = 'api_error';
  const REASON_NOT_CONFIGURED = 'not_configured';

  customElements.define(
    'discount-code-capture',
    class DiscountCodeCapture extends HTMLElement {
      constructor() {
        super();

        this.onSubmit = this.handleSubmit.bind(this);
      }

      connectedCallback() {
        this.form = this.querySelector('[data-discount-form]');
        this.input = this.querySelector('input[name="discount-code"]');
        this.submitButton = this.querySelector('[data-submit]');
        this.errorElement = this.querySelector('[data-error]');
        this.errorMessageElement = this.querySelector('[data-error-message]');
        this.successElement = this.querySelector('[data-success]');

        this.form?.addEventListener('submit', this.onSubmit);
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.form?.removeEventListener('submit', this.onSubmit);
      }

      get apiUrl() {
        return this.getAttribute('data-api-url');
      }

      get token() {
        return this.getAttribute('data-token');
      }

      get forwardUrl() {
        return this.getAttribute('data-forward-url');
      }

      get paramName() {
        return this.getAttribute('data-param-name') || 'preferredCode';
      }

      get cookieName() {
        return this.getAttribute('data-cookie-name') || 'xyon_preferred_code';
      }

      get cookieDomain() {
        return this.getAttribute('data-cookie-domain') || '';
      }

      get cookieExpiryDays() {
        const days = parseInt(this.getAttribute('data-cookie-expiry'), 10);
        return Number.isFinite(days) && days > 0 ? days : 30;
      }

      get throttleMs() {
        const ms = parseInt(this.getAttribute('data-throttle'), 10);
        return Number.isFinite(ms) && ms >= 0 ? ms : 500;
      }

      get bucketSuffix() {
        return (this.getAttribute('data-bucket-suffix') || '').trim();
      }

      get genericError() {
        return this.getAttribute('data-error-generic') || '';
      }

      // The blocking CartWarningCode set (these keys) mapped to their configured messages. Any code
      // is rejected only if it appears here; everything else — no warning, or product/cart/customer
      // applicability — is accepted, since web-ui-v3 is the sole downstream product-applicability check.
      get blockMessages() {
        const notFound = this.getAttribute('data-error-not-found') || this.genericError;
        const inactive = this.getAttribute('data-error-inactive') || this.genericError;
        const generic = this.genericError;
        return {
          DISCOUNT_NOT_FOUND: notFound,
          DISCOUNT_CURRENTLY_INACTIVE: inactive,
          DISCOUNT_USAGE_LIMIT_REACHED: generic,
          DISCOUNT_CUSTOMER_USAGE_LIMIT_REACHED: generic,
          DISCOUNT_CODE_NOT_HONOURED: generic
        };
      }

      get isDesignMode() {
        return Boolean(window.Shopify && window.Shopify.designMode);
      }

      // Active bucket variation used for validation: base code + active suffix (no double-append).
      // The suffix check is case-insensitive because Shopify applies codes case-insensitively, so a
      // visitor typing an already-suffixed code in any case must not have the suffix appended again.
      withSuffix(code) {
        const suffix = this.bucketSuffix;
        if (!suffix || code.toLowerCase().endsWith(suffix.toLowerCase())) return code;
        return `${code}${suffix}`;
      }

      async handleSubmit(event) {
        event.preventDefault();

        const now = Date.now();
        if (this.lastSubmitAt && now - this.lastSubmitAt < this.throttleMs) return;
        this.lastSubmitAt = now;

        const code = (this.input?.value || '').trim();
        if (code === '') return;

        // Validate the active bucket variation (base + suffix), but forward the base code — web-ui-v3
        // resolves the live bucket and applies the highest active discount.
        const validationCode = this.withSuffix(code);

        if (!this.token || !this.apiUrl) {
          this.pushValidationResult(OUTCOME_ERROR, code, validationCode, REASON_NOT_CONFIGURED);
          this.showError(this.genericError);
          return;
        }

        this.clearMessages();
        this.setBusy(true);

        this.abortController?.abort();
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        try {
          const cartId = await this.createCart(signal);
          const warnings = await this.applyDiscount(cartId, validationCode, signal);

          const messages = this.blockMessages;
          const blocked = warnings.find((warning) => warning && Object.prototype.hasOwnProperty.call(messages, warning.code));
          if (blocked) {
            this.pushValidationResult(OUTCOME_REJECTED, code, validationCode, blocked.code);
            this.showError(messages[blocked.code] || this.genericError);
            return;
          }

          this.accept(code, validationCode);
        } catch (error) {
          if (error?.name === 'AbortError') return;
          console.error('[discount-code-capture]', error);
          this.pushValidationResult(OUTCOME_ERROR, code, validationCode, REASON_API_ERROR);
          this.showError(this.genericError);
        } finally {
          this.setBusy(false);
        }
      }

      async createCart(signal) {
        // Empty throwaway cart — separate from the visitor's live cart, which is never touched.
        // Discount existence/validity warnings are line-independent; if a discount ever needs a
        // line item to evaluate, seed one minimal placeholder line in this input.
        const query = 'mutation { cartCreate(input: {}) { cart { id } userErrors { message } } }';
        const data = await this.graphql(query, {}, signal);
        const payload = data.cartCreate;
        if (!payload?.cart?.id || payload.userErrors?.length) {
          throw new Error('cartCreate failed');
        }
        return payload.cart.id;
      }

      async applyDiscount(cartId, code, signal) {
        // Discount warnings are exposed on the mutation payload, not as a field on the Cart type.
        const query = [
          'mutation discountUpdate($cartId: ID!, $codes: [String!]!) {',
          '  cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $codes) {',
          '    warnings { code message target }',
          '    userErrors { message }',
          '  }',
          '}'
        ].join('\n');

        const data = await this.graphql(query, { cartId, codes: [code] }, signal);
        const payload = data.cartDiscountCodesUpdate;
        if (!payload || payload.userErrors?.length) {
          throw new Error('cartDiscountCodesUpdate failed');
        }

        return payload.warnings || [];
      }

      async graphql(query, variables, signal) {
        const response = await fetch(this.apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Shopify-Storefront-Access-Token': this.token
          },
          body: JSON.stringify({ query, variables }),
          signal
        });

        if (!response.ok) throw new Error(`Storefront API HTTP ${response.status}`);

        const json = await response.json();
        if (json.errors?.length) throw new Error(json.errors[0].message || 'Storefront API error');
        if (!json.data) throw new Error('Storefront API returned no data');
        return json.data;
      }

      accept(code, validationCode) {
        // In the theme editor, preview the success state without writing a cookie or navigating away.
        if (this.isDesignMode) {
          this.showSuccess();
          return;
        }

        const url = this.buildForwardUrl(code);
        if (!url) {
          // Nothing to forward to — don't strand the visitor on a dead-end success state.
          this.pushValidationResult(OUTCOME_ERROR, code, validationCode, REASON_NOT_CONFIGURED);
          this.showError(this.genericError);
          return;
        }

        this.setCookie(code);
        this.showSuccess();
        this.pushValidationResult(OUTCOME_ACCEPTED, code, validationCode, null);
        window.setTimeout(() => window.location.assign(url), 600);
      }

      // Report the validation outcome to GTM. dataLayer is created by the container snippet in
      // layout/theme.liquid; guard anyway so a blocked or absent container can't break validation.
      // Codes are upper-cased so case variants group together in reporting (Shopify applies codes
      // case-insensitively). Reasons are lower-cased so Shopify's SCREAMING_SNAKE CartWarningCode
      // values read the same as our own reasons and the outcome values alongside them.
      pushValidationResult(outcome, code, validationCode, reason) {
        // handleSubmit runs to completion in the theme editor, so without this every test submit
        // there would land in production analytics.
        if (this.isDesignMode) return;

        // Reporting must never break the forward. Once GTM loads it replaces dataLayer.push with its
        // own function, and other installed apps touch dataLayer too — so this can throw for reasons
        // that have nothing to do with us, and the accept path would strand the visitor mid-success.
        try {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: DATALAYER_EVENT_RESULT,
            preferred_code: code.toUpperCase(),
            preferred_code_variation: validationCode.toUpperCase(),
            preferred_code_outcome: outcome,
            preferred_code_reason: reason ? reason.toLowerCase() : null
          });

          // GTM merges each push into one persistent model, so without this the fields above stay
          // resolvable for every later event on the page — on the rejected path the visitor stays
          // put, and a rejected code would silently attach itself to unrelated hits. Tags for the
          // event above have already read their values by the time this lands, since GTM processes
          // queued messages in order. Every key cleared here is preferred_code-prefixed: this store
          // runs several apps that write to dataLayer, and clearing a generic key like `outcome`
          // would wipe a value we don't own.
          window.dataLayer.push({
            preferred_code: undefined,
            preferred_code_variation: undefined,
            preferred_code_outcome: undefined,
            preferred_code_reason: undefined
          });
        } catch (error) {
          console.error('[discount-code-capture]', error);
        }
      }

      buildForwardUrl(code) {
        const base = this.forwardUrl;
        if (!base) return '';
        const separator = base.indexOf('?') === -1 ? '?' : '&';
        return `${base}${separator}${encodeURIComponent(this.paramName)}=${encodeURIComponent(code)}`;
      }

      setCookie(code) {
        const maxAge = this.cookieExpiryDays * 24 * 60 * 60;
        const parts = [`${this.cookieName}=${encodeURIComponent(code)}`, `max-age=${maxAge}`, 'path=/', 'samesite=lax'];

        // Only scope to the configured domain when the current host is within it; otherwise the
        // browser silently drops the cookie. (The ?preferredCode= param is the cross-domain handoff.)
        const registrableDomain = this.cookieDomain.replace(/^\./, '');
        const host = window.location.hostname;
        if (registrableDomain && (host === registrableDomain || host.endsWith(`.${registrableDomain}`))) {
          parts.push(`domain=${this.cookieDomain}`);
        }

        if (window.location.protocol === 'https:') parts.push('secure');
        document.cookie = parts.join('; ');
      }

      setBusy(state) {
        if (state) this.submitButton?.setAttribute('aria-busy', 'true');
        else this.submitButton?.removeAttribute('aria-busy');
        if (this.submitButton) this.submitButton.disabled = state;
        if (this.input) this.input.disabled = state;
      }

      clearMessages() {
        this.hide(this.errorElement);
        this.input?.removeAttribute('aria-invalid');
      }

      showError(message) {
        if (this.errorMessageElement) this.errorMessageElement.textContent = message || '';
        this.show(this.errorElement);
        this.input?.setAttribute('aria-invalid', 'true');
      }

      showSuccess() {
        this.hide(this.errorElement);
        this.input?.removeAttribute('aria-invalid');
        this.show(this.successElement);
      }

      show(element) {
        if (element) element.hidden = false;
      }

      hide(element) {
        if (element) element.hidden = true;
      }
    }
  );
}
